import { useSortable } from "@dnd-kit/sortable";
import { DotsGrid } from "@untitledui/icons";
import type { ReactNode } from "react";

export interface DraggableColumnContentProps {
	/** The column ID for sortable context */
	columnId: string;
	/** Children to render inside the header */
	children: ReactNode;
	/** Additional className for the wrapper */
	className?: string;
	/** When true, column is not draggable and no drag handle is shown */
	disabled?: boolean;
}

/**
 * DraggableColumnContent wraps table header content to make it draggable
 * using dnd-kit. Works inside React Aria's Column component.
 */
export function DraggableColumnContent({
	columnId,
	children,
	className,
	disabled = false,
}: DraggableColumnContentProps) {
	const { attributes, isDragging, listeners, setNodeRef } = useSortable({
		id: columnId,
		disabled,
	});

	return (
		<div
			ref={setNodeRef}
			className={`flex items-center gap-1 ${className ?? ""}`}
			style={{
				opacity: isDragging ? 0.5 : 1,
				transition: "none",
			}}
		>
			{disabled ? null : (
				<button
					type="button"
					className="flex-shrink-0 cursor-grab active:cursor-grabbing text-fg-quaternary hover:text-fg-secondary touch-none"
					{...attributes}
					{...listeners}
				>
					<DotsGrid size={14} />
				</button>
			)}
			{children}
		</div>
	);
}

/** Static header content for DragOverlay */
export interface DragOverlayHeaderProps {
	children: ReactNode;
}

export function DragOverlayHeader({ children }: DragOverlayHeaderProps) {
	return (
		<div className="flex items-center bg-primary justify-center px-4 py-2 shadow-lg rounded border border-secondary">
			{children}
		</div>
	);
}
